/**
 * "CANOPY HAS BEEN UPDATED." (Jeremy, 1 Nov 2026, punch list F173; the same words as Fieldpath's.)
 *
 * After a promotion, anybody with the scheduler already open keeps running the old build until
 * they reload. So the page asks, now and then, which build the server hands out: it fetches `/`
 * with `cache: "no-store"` and compares the hashed module script it names with the one this page
 * is running. When they differ, a small bar at the foot of the window says so, with one button,
 * Reload. Nothing reloads by itself: somebody halfway through a request would lose it.
 *
 * Never in the first minute after loading; then every five minutes while the tab is visible, and
 * on coming back to it. A failed fetch, a page that is not the app, or a dev server (whose script
 * is `/src/main.tsx`) is "cannot tell", never "newer". The rules and their tests:
 * `@scheduler/shared` (refresh.ts).
 */
import { buildScripts, CHECK_EVERY_MS, isNewerBuild, mayCheck, moduleScripts } from "@scheduler/shared";
import { useEffect, useState } from "react";

async function servedScripts(): Promise<string[] | null> {
  try {
    const res = await fetch("/", { cache: "no-store", credentials: "same-origin" });
    if (!res.ok) return null;
    return moduleScripts(await res.text());
  } catch {
    return null;
  }
}

function useNewerBuild(): boolean {
  const [newer, setNewer] = useState(false);

  useEffect(() => {
    if (newer) return;
    const loadedAt = Date.now();
    const running = Array.from(document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')).map(
      (s) => s.getAttribute("src") ?? "",
    );
    // A dev server, or a page with no hashed script: nothing to compare.
    if (buildScripts(running).length === 0) return;
    let stopped = false;
    let asking = false;

    const check = async (): Promise<void> => {
      if (stopped || asking) return;
      if (!mayCheck(Date.now(), loadedAt, document.visibilityState === "visible")) return;
      asking = true;
      const served = await servedScripts();
      asking = false;
      if (!stopped && served && isNewerBuild(running, served)) setNewer(true);
    };

    const timer = window.setInterval(() => void check(), CHECK_EVERY_MS);
    const onVisible = (): void => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [newer]);

  return newer;
}

export function UpdateBar() {
  const newer = useNewerBuild();
  if (!newer) return null;
  return (
    <div className="update-bar" role="status">
      <span>Canopy has been updated.</span>
      <button type="button" className="primary narrow" onClick={() => window.location.reload()}>
        Reload
      </button>
    </div>
  );
}
